<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { HistoryAttachment } from '@/types/historyAttachment';

const props = defineProps<{
  fromAttachments?: HistoryAttachment[];
  toAttachments?: HistoryAttachment[];
}>();

const { t } = useI18n();

const isImage = (type: string) => type?.includes('image');

const formatFileName = (name: string) => {
  if (name.length > 20)
    return name.substring(0, 15) + '...' + name.split('.').pop();
  return name;
};

type AttachmentDiffItem = {
  key: string;
  from?: HistoryAttachment;
  to?: HistoryAttachment;
};

const isSameAttachment = (
  from?: HistoryAttachment,
  to?: HistoryAttachment
): boolean => {
  if (!from || !to) {
    return false;
  }

  return (
    from.file_url === to.file_url &&
    from.file_name === to.file_name &&
    from.file_size === to.file_size &&
    from.content_type === to.content_type
  );
};

const buildDiffItems = (
  fromList: HistoryAttachment[] = [],
  toList: HistoryAttachment[] = []
): AttachmentDiffItem[] => {
  const hasBackendPairing = fromList.some(a => a.replaced_attachment_id);

  if (hasBackendPairing) {
    const toMapById = new Map(toList.map(a => [a.id, a]));

    return fromList
      .map(from => {
        const replacedId = from.replaced_attachment_id;
        if (!replacedId) {
          return undefined;
        }

        return {
          key: `replaced-${from.id}`,
          from,
          to: toMapById.get(replacedId),
        };
      })
      .filter(Boolean)
      .filter(item => {
        const typedItem = item as AttachmentDiffItem;

        if (!typedItem.from || !typedItem.to) {
          return false;
        }

        return !isSameAttachment(typedItem.from, typedItem.to);
      }) as AttachmentDiffItem[];
  }

  const fromMap = new Map(fromList.map(a => [a.id, a]));
  const toMap = new Map(toList.map(a => [a.id, a]));

  const matchedIds = Array.from(
    new Set(
      [...fromMap.keys(), ...toMap.keys()].filter(
        id => fromMap.has(id) && toMap.has(id)
      )
    )
  );

  const matchedItems: AttachmentDiffItem[] = matchedIds.map(id => ({
    key: String(id),
    from: fromMap.get(id),
    to: toMap.get(id),
  }));

  const removedItems: AttachmentDiffItem[] = fromList
    .filter(a => !toMap.has(a.id))
    .map(a => ({ key: `from-${a.id}`, from: a }));

  const addedItems: AttachmentDiffItem[] = toList
    .filter(a => !fromMap.has(a.id))
    .map(a => ({ key: `to-${a.id}`, to: a }));

  return [...matchedItems, ...removedItems, ...addedItems].filter(item => {
    if (!item.from || !item.to) {
      return true;
    }

    return !isSameAttachment(item.from, item.to);
  });
};

const diffItems = buildDiffItems(
  props.fromAttachments ?? [],
  props.toAttachments ?? []
);
</script>

<template>
  <div class="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
    <div v-if="diffItems.length" class="flex gap-2 overflow-x-auto pb-1">
      <div
        v-for="item in diffItems"
        :key="item.key"
        class="flex items-center gap-2 shrink-0 border border-slate-200 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-800/50 p-2"
      >
        <a
          v-if="item.from"
          :href="item.from.file_url"
          target="_blank"
          rel="noopener noreferrer"
          class="group flex items-center rounded-md overflow-hidden hover:border-blue-500 transition-colors"
          :class="isImage(item.from.content_type) ? 'p-0' : 'gap-2'"
        >
          <img
            v-if="isImage(item.from.content_type)"
            :src="item.from.file_url"
            class="h-10 w-10 object-cover rounded"
            :alt="item.from.file_name"
          />
          <div v-else class="flex items-center gap-2">
            <div
              class="flex items-center justify-center w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded text-slate-500"
            >
              <i class="i-lucide-file-text size-4" />
            </div>
            <div class="flex flex-col">
              <span
                class="text-xs font-medium text-slate-600 dark:text-slate-200 truncate max-w-[120px]"
              >
                {{ formatFileName(item.from.file_name) }}
              </span>
            </div>
          </div>
        </a>
        <div v-else class="text-xs italic text-slate-400">
          {{ t('CONVERSATION.EDIT_HISTORY.ATTACHMENT_ADDED') }}
        </div>

        <i class="i-lucide-arrow-right size-4 text-slate-400" />

        <a
          v-if="item.to"
          :href="item.to.file_url"
          target="_blank"
          rel="noopener noreferrer"
          class="group flex items-center rounded-md overflow-hidden hover:border-blue-500 transition-colors"
          :class="isImage(item.to.content_type) ? 'p-0' : 'gap-2'"
        >
          <img
            v-if="isImage(item.to.content_type)"
            :src="item.to.file_url"
            class="h-10 w-10 object-cover rounded"
            :alt="item.to.file_name"
          />
          <div v-else class="flex items-center gap-2">
            <div
              class="flex items-center justify-center w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded text-slate-500"
            >
              <i class="i-lucide-file-text size-4" />
            </div>
            <div class="flex flex-col">
              <span
                class="text-xs font-medium text-blue-600 truncate max-w-[120px]"
              >
                {{ formatFileName(item.to.file_name) }}
              </span>
            </div>
          </div>
        </a>
        <div v-else class="text-xs italic text-slate-400">
          {{ t('CONVERSATION.EDIT_HISTORY.ATTACHMENT_REMOVED') }}
        </div>
      </div>
    </div>
    <div v-else class="text-xs italic text-slate-400">
      {{ t('CONVERSATION.EDIT_HISTORY.ATTACHMENTS_UNCHANGED') }}
    </div>
  </div>
</template>
